/**
 * OfficerCard Component
 *
 * Displays officer profile card with name, title, key achievements preview,
 * and button to open full biography dialog
 */

import React from 'react'
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import { Officer } from '@/data/officers'

export interface OfficerCardProps {
  /**
   * Officer profile data
   */
  officer: Officer

  /**
   * Callback when "Read Full Bio" button is clicked
   */
  onReadBio: (officer: Officer) => void
}

/**
 * OfficerCard Component
 */
export const OfficerCard: React.FC<OfficerCardProps> = ({
  officer,
  onReadBio,
}) => {
  // Show first 3 achievements on card
  const previewAchievements = officer.keyAchievements.slice(0, 3)
  const hasMoreAchievements = officer.keyAchievements.length > 3

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      {/* Profile Image */}
      {officer.profileImage && (
        <CardMedia
          component="img"
          image={officer.profileImage}
          alt={`${officer.name} - ${officer.title}`}
          sx={{
            height: 300,
            objectFit: 'cover',
          }}
        />
      )}

      <CardContent sx={{ flexGrow: 1, p: 4 }}>
        {/* Officer Name */}
        <Typography
          variant="h4"
          component="h3"
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.5rem', md: '1.75rem' },
            color: 'primary.main',
            mb: 1,
          }}
        >
          {officer.name}
        </Typography>

        {/* Officer Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: 'secondary.main',
            mb: 3,
          }}
        >
          {officer.title}
        </Typography>

        {/* Key Achievements Preview */}
        {previewAchievements.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 1.5,
                color: 'text.primary',
              }}
            >
              Key Achievements
            </Typography>
            <List
              sx={{
                listStyleType: 'disc',
                pl: 3,
                '& .MuiListItem-root': {
                  display: 'list-item',
                  py: 0.5,
                  px: 0,
                },
              }}
            >
              {previewAchievements.map((achievement, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={achievement}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: 'text.secondary',
                      sx: { lineHeight: 1.6 },
                    }}
                  />
                </ListItem>
              ))}
            </List>
            {hasMoreAchievements && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  mt: 1,
                  pl: 3,
                }}
              >
                + {officer.keyAchievements.length - 3} more achievements
              </Typography>
            )}
          </Box>
        )}

        {/* Primary Affiliation */}
        {officer.affiliations && officer.affiliations.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: 'text.primary',
              }}
            >
              Affiliations
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.6,
              }}
            >
              {officer.affiliations[0]}
              {officer.affiliations.length > 1 && (
                <Box
                  component="span"
                  sx={{ fontStyle: 'italic', color: 'text.disabled' }}
                >
                  {' '}
                  + {officer.affiliations.length - 1} more
                </Box>
              )}
            </Typography>
          </Box>
        )}

        {/* Read Full Bio Button */}
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          onClick={() => onReadBio(officer)}
          aria-label={`Read full biography of ${officer.name}`}
          sx={{
            mt: 'auto',
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1rem',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3,
            },
          }}
        >
          Read Full Bio
        </Button>
      </CardContent>
    </Card>
  )
}

export default OfficerCard
